import React, { Suspense, useMemo, useRef, useState } from 'react';
import { AxesHelper, Setup, Grid, VirtualCourse, Loader } from '@slope/r3f';
import { NavMeshHelper } from '@slope/navmesh/r3f';
import { EffectComposer, Outline, SMAA, ToneMapping } from '@react-three/postprocessing';
import { DropZone } from '@slope/uikit';
import { useThree } from '@react-three/fiber';
import { Bvh, CameraControls, GizmoHelper, GizmoViewport } from '@react-three/drei';
import { button, folder, useControls } from 'leva';
import * as THREE from 'three';
import { useMutableCallback } from '../../hooks';
import { calculateFitArgs } from '../../utils';
import { Pivot } from '../Pivot';
import { Hole } from '@slope/three';
import { useStore } from '@slope/store';

export type SceneProps = {
  src: string;
  onLoad?: (object: THREE.Object3D) => void;
};

const raycaster = new THREE.Raycaster();
const v2 = new THREE.Vector2();

const Pivots: React.FC = () => {
  const camera = useThree(({ camera }) => camera);
  const scene = useThree(({ scene }) => scene);
  const [pivots, setPivots] = useState<THREE.Vector3[]>([]);

  useControls({
    pivots: folder({
      'Add Pivot': button(() => {
        raycaster.setFromCamera(v2.setScalar(0), camera);
        const intersects = raycaster.intersectObjects(scene.children, true);
        const point = intersects[0]?.point || new THREE.Vector3(0, 0, 0);
        setPivots((prev) => [...prev, point.clone()]);
      }),
    }),
  });

  return (
    <group>
      {pivots.map((pos, i) => (
        <Pivot
          key={i}
          position={pos}
          onUpdate={(v) => {
            setPivots((prev) => prev.map((p, j) => (i === j ? v : p)));
          }}
        />
      ))}
    </group>
  );
};

const Scene: React.FC<SceneProps> = ({ src, onLoad }) => {
  const [holeNames, setHoleNames] = useState<string[]>([]);
  const { hole } = useControls(
    {
      hole: { value: 'all', options: ['all', ...holeNames] },
    },
    [holeNames],
  );

  const handleLoad = useMutableCallback((object: THREE.Object3D, holeNames: string[]) => {
    setHoleNames(holeNames);
    onLoad?.(object);
  });

  return <VirtualCourse src={src} hole={hole} onLoad={handleLoad} clone />;
};

export const Viewer = () => {
  const cameraControlsRef = useRef<CameraControls>(null);
  const [modelSrc, setModelSrc] = useState<string | null>(null);
  const storeSet = useStore(({ set }) => set);
  const scene = useStore(({ scene }) => scene);
  const selection = useStore(({ selection }) => selection);

  const { showGrid, showAxes, showNavMesh, navMeshFlags } = useControls({
    helpers: folder({
      showGrid: { value: true, label: 'Show Grid' },
      showAxes: { value: true, label: 'Show Axes' },
      showNavMesh: { value: false, label: 'Show Navmesh' },
    }),
    camera: folder({
      'Got to origin': button(() =>
        cameraControlsRef.current?.setLookAt(100, 100, 100, 0, 0, 0, true),
      ),
    }),
    navMeshFlags: {
      label: 'Marker Flags',
      value: 0xffff,
      options: {
        All: 0xffff,
        Green: 1 << 1,
        Tee: 1 << 2,
        Bunker: 1 << 3,
        Semi: 1 << 4,
        Fairway: 1 << 5,
        Rough: 1 << 6,
        Path: 1 << 7,
      },
    },
  });

  const handleLoad = useMutableCallback((object: THREE.Object3D) => {
    const { position, target } = calculateFitArgs(object, cameraControlsRef.current!.camera, 1.2);

    cameraControlsRef.current?.setLookAt(
      position.x,
      position.y,
      position.z,
      target.x,
      target.y,
      target.z,
      true,
    );

    storeSet({
      scene: object,
      controls: cameraControlsRef.current,
    });
  });

  const outlinePattern = useMemo(() => {
    const width = 2;
    const height = 2;
    const size = width * height;
    const data = new Uint8Array(4 * size);
    const color = new THREE.Color(0xffa500); // orange color
    const r = Math.floor(color.r * 255);
    const g = Math.floor(color.g * 255);
    const b = Math.floor(color.b * 255);
    for (let i = 0; i < size; i++) {
      const stride = i * 4;
      data[stride] = r;
      data[stride + 1] = g;
      data[stride + 2] = b;
      data[stride + 3] = 200;
    }
    // used the buffer to create a [name]
    const texture = new THREE.DataTexture(data, width, height);
    texture.needsUpdate = true;
    return texture;
  }, []);

  if (!modelSrc) {
    return (
      <DropZone
        message={'Drag and dop a glb file to load a scene'}
        accept={{
          'model/gltf-binary': ['.glb'],
        }}
        onDrop={(arrayBuffer) => {
          setModelSrc(URL.createObjectURL(new Blob([arrayBuffer])));
        }}
      />
    );
  }
  console.log('Loading model:', (scene as Hole).navMesh);

  return (
    <Setup
      frameloop="always"
      controls={false}
      gl={{ antialias: false }}
      camera={{
        position: [0, 150, 0],
        fov: 60,
        near: 1,
        far: 5000,
      }}
    >
      <directionalLight position={[0, 10, 0]} intensity={1} castShadow />
      <Pivots />
      <Bvh>
        <Suspense fallback={<Loader message={'Loading Model...'} />}>
          <Scene src={modelSrc} onLoad={handleLoad} />
        </Suspense>
      </Bvh>
      {showNavMesh && (scene as Hole).navMesh && (
        <NavMeshHelper navMesh={(scene as Hole).navMesh} flags={navMeshFlags}>
          <meshBasicMaterial color="blue" depthTest={false} />
        </NavMeshHelper>
      )}
      {showGrid && <Grid size={1000} position={[0, -1, 0]} />}
      {showAxes && <AxesHelper scale={10} />}

      <CameraControls ref={cameraControlsRef} makeDefault smoothTime={1} />
      <EffectComposer autoClear={false}>
        <>
          {!!selection?.length && (
            <Outline
              blur
              edgeStrength={1}
              xRay
              selection={selection}
              patternTexture={outlinePattern}
              visibleEdgeColor={0xffa500}
              hiddenEdgeColor={0xaa8500}
            />
          )}
          <SMAA />
          <ToneMapping />
        </>
      </EffectComposer>
      <GizmoHelper alignment="bottom-right" margin={[80, 80]} renderPriority={2}>
        <GizmoViewport />
      </GizmoHelper>
    </Setup>
  );
};
